import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Application from "expo-application";

import analytics from "@/analytics";
import { useAuth } from "@/components/AuthContext";
import bundled from "./entries.json";
import { entriesFor, mergeSeen, normalizeFeed, unreadIds } from "./logic";

// What's new for business accounts: the feed, which entries each broadcaster
// has seen, and the hooks the header button, the dropdown, the Profile row
// and the What's new screen share. Rules: docs/whats-new.md.
//
// - The feed is components/whatsNew/entries.json. The web app ships with it;
//   the native apps also fetch the copy the web build publishes
//   (FEED_URL), so a new entry reaches them without a store release.
// - "Seen" is kept per broadcaster on this device (AsyncStorage, localStorage
//   on the web), so two accounts on one phone each get their own counter.

export const FEED_URL = "https://broadcaster.eventlive.pro/whats-new.json";
const FEED_CACHE_KEY = "eventlive.whatsnew.feed.v1";
const FEED_REFRESH_MS = 6 * 60 * 60 * 1000;
const seenKey = (owner) =>
  `eventlive.whatsnew.v1.${encodeURIComponent(owner)}`;

let state = { feed: normalizeFeed(bundled), owner: null, seen: null };
const listeners = new Set();
const emit = (next) => {
  state = { ...state, ...next };
  listeners.forEach((fn) => fn());
};
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const getState = () => state;

async function loadSeen(owner) {
  if (state.owner === owner) return;
  emit({ owner, seen: null });
  let seen = [];
  try {
    const raw = await AsyncStorage.getItem(seenKey(owner));
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed?.seen)) {
      seen = parsed.seen.filter((id) => typeof id === "string");
    }
  } catch {
    // Private browsing or a broken value: start with nothing seen.
  }
  if (state.owner === owner) emit({ seen });
}

export function markSeen(ids) {
  const { owner, seen, feed } = state;
  if (!owner || !Array.isArray(seen) || !ids?.length) return;
  if (ids.every((id) => seen.includes(id))) return;
  const next = mergeSeen(
    seen,
    ids,
    feed.map((e) => e.id)
  );
  emit({ seen: next });
  AsyncStorage.setItem(
    seenKey(owner),
    JSON.stringify({ seen: next, at: new Date().toISOString() })
  ).catch(() => {});
}

let feedCheckedAt = 0;
let feedLoading = false;
const applyFeed = (json) => {
  const entries = normalizeFeed(json);
  if (entries.length) emit({ feed: entries });
  return entries.length > 0;
};

// Native apps only: the web app is always deployed with the latest feed.
async function refreshFeed() {
  if (Platform.OS === "web" || feedLoading) return;
  if (feedCheckedAt && Date.now() - feedCheckedAt < FEED_REFRESH_MS) return;
  feedLoading = true;
  try {
    if (!feedCheckedAt) {
      const cached = await AsyncStorage.getItem(FEED_CACHE_KEY).catch(
        () => null
      );
      if (cached) {
        try {
          applyFeed(JSON.parse(cached));
        } catch {}
      }
    }
    const res = await fetch(FEED_URL, { headers: { Accept: "application/json" } });
    if (res.ok) {
      const json = await res.json();
      if (applyFeed(json)) {
        AsyncStorage.setItem(FEED_CACHE_KEY, JSON.stringify(json)).catch(
          () => {}
        );
      }
    }
  } catch {
    // Offline: keep the bundled or cached feed.
  } finally {
    feedCheckedAt = Date.now();
    feedLoading = false;
  }
}

const platform = () =>
  Platform.OS === "ios" || Platform.OS === "android" ? Platform.OS : "web";

// The feed for the signed-in broadcaster. `enabled` is false until the
// profile has loaded, and for individual accounts (What's new is B2B only).
export function useWhatsNew() {
  const {
    profileLoaded,
    account_type,
    broadcaster,
    broadcaster_role,
    registered_datetime,
  } = useAuth();
  const enabled = !!profileLoaded && account_type === "business" && !!broadcaster;
  const snap = useSyncExternalStore(subscribe, getState, getState);

  useEffect(() => {
    if (!enabled) return;
    loadSeen(broadcaster);
    refreshFeed();
  }, [enabled, broadcaster]);

  const entries = useMemo(
    () =>
      enabled
        ? entriesFor(snap.feed, {
            role: broadcaster_role,
            platform: platform(),
            appVersion:
              Platform.OS === "web"
                ? null
                : Application.nativeApplicationVersion,
          })
        : [],
    [enabled, snap.feed, broadcaster_role]
  );
  const ready = enabled && snap.owner === broadcaster && Array.isArray(snap.seen);
  const unread = useMemo(
    () => (ready ? unreadIds(entries, snap.seen, registered_datetime) : []),
    [ready, entries, snap.seen, registered_datetime]
  );
  return { enabled, ready, entries, unread, unreadCount: unread.length };
}

// For a view that shows the list (dropdown or screen): everything unread
// when it opened stays highlighted while it's open, and counts as read from
// then on, so the counter clears and the next visit shows it plainly.
export function useWhatsNewSession(source) {
  const wn = useWhatsNew();
  const [fresh, setFresh] = useState(null);
  const unreadKey = wn.unread.join(",");

  useEffect(() => {
    if (!wn.ready) return;
    const first = fresh === null;
    if (first) {
      analytics.track("What's New Opened", {
        source,
        unread_count: wn.unread.length,
        entry_count: wn.entries.length,
      });
    }
    if (first || wn.unread.length) {
      setFresh((prev) => new Set([...(prev || []), ...wn.unread]));
    }
    markSeen(wn.unread);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wn.ready, unreadKey]);

  return { ...wn, fresh: fresh || new Set() };
}
